import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import FileUpload from "../../components/FileUpload";
import Input from "../../components/input";
import Select from "../../components/Select";
import { clearVehicleDetail, createServiceEntry, fetchServiceTagsDropdown, fetchVehicleDetail, fetchVehicleDropdown } from "../../store/Slices/ServiceSlice";
import "../../styles/ServiceEntry.css";
import { maintenanceTypesDropdown, repairPriorityDropdown } from "../../utils/Dropdowns";
import { slugfy } from "../../utils/helper";
import { serviceEntryValidationSchema } from "../../utils/Schema";
import { errorNotify, successNotify } from "../../utils/toast";
import IssueTracking from "./IssueTracking";
import ServiceLineItem from "./ServiceLineItem";

const ServiceEntry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [link_issue_ids, setLink_issue_ids] = useState([]);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [taxPercent, setTaxPercent] = useState(0);
  const [checkedIssueIds, setCheckedIssueIds] = useState(() => {
    // Load from localStorage on mount
    const saved = localStorage.getItem(`checkedIssueIds_new`);
    return saved ? JSON.parse(saved) : [];
  });

  const {
    loading,
    vehicleData,
    vehicleDetail,
    vehicleDetailLoading,
    createServiceLoading,
    serviceTagsData,
    serviceTagsLoading,
  } = useSelector((state) => state.services);

  // Save to localStorage whenever checkedIssueIds changes
  useEffect(() => {
    const storageKey = `checkedIssueIds_new`;
    localStorage.setItem(storageKey, JSON.stringify(checkedIssueIds));
  }, [checkedIssueIds]);

  useEffect(() => {
    dispatch(fetchVehicleDropdown());
    dispatch(fetchServiceTagsDropdown())

    return () => {
      dispatch(clearVehicleDetail());
      // Clear localStorage on unmount
      const storageKey = `checkedIssueIds_new`;
      localStorage.removeItem(storageKey);
    };
  }, []);

  const tagsOptions = serviceTagsData?.map(tag => ({ label: tag.tag_name, value: tag.tag_name }));

  const handleVehicleChange = async (selectedOption, setFieldValue) => {
    setSelectedVehicle(selectedOption);
    setFieldValue("vehicle_id", selectedOption?.value || "");

    if (selectedOption) {
      const response = await dispatch(fetchVehicleDetail({
        vehicleId: selectedOption.value,
        vehicleBrandId: selectedOption.brand_id,
        ref_id: selectedOption.ref_id
      })).unwrap();

      setFieldValue('odo_meter', response?.data?.last_service?.odo_meter || null);
      setFieldValue('odm', response?.data?.odm || '');
    }
  };

  const calculateCostSummary = () => {
    if (!vehicleDetail?.vehicle_service_line_items) {
      return {
        cost_parts: 0,
        cost_labor: 0,
        sub_total: 0,
      };
    }

    const items = vehicleDetail.vehicle_service_line_items;
    const cost_parts = items.reduce(
      (sum, item) => sum + parseFloat(item.cost_parts || 0),
      0
    );
    const cost_labor = items.reduce(
      (sum, item) => sum + parseFloat(item.cost_labor || 0),
      0
    );
    const sub_total = cost_parts + cost_labor;

    return {
      cost_parts: cost_parts.toFixed(2),
      cost_labor: cost_labor.toFixed(2),
      sub_total: sub_total.toFixed(2),
    };
  };

  const costSummary = calculateCostSummary();

  const getRefNumber = `SE-${slugfy(vehicleDetail?.registration_number)}-${parseInt(vehicleDetail?.max_id) + 1}`;

  const initialValues = {
    vehicle_id: selectedVehicle?.value || "",
    odo_meter: vehicleDetail?.odm || "",
    odm: vehicleDetail?.odm || "",
    ref_number: vehicleDetail?.registration_number
      ? getRefNumber.toUpperCase() || ""
      : "",
    status: "open",
    priority: "",
    services_type: "",
    start_at: "",
    complete_at: "",
    note: "",
    tags: [],
  };

  const handleDiscountChange = (e) => {
    let val = e.target.value.replace(/[^0-9.]/g, '');
    val = val === '' ? '' : Math.max(0, Math.min(100, Number(val)));
    setDiscountPercent(val);
  };
  const handleTaxChange = (e) => {
    let val = e.target.value.replace(/[^0-9.]/g, '');
    val = val === '' ? '' : Math.max(0, Math.min(100, Number(val)));
    setTaxPercent(val);
  };

  const calculateTotal = () => {
    const subTotal = parseFloat(costSummary.sub_total);
    const discount = parseFloat(discountPercent) || 0;
    const tax = parseFloat(taxPercent) || 0;
    const discountAmount = subTotal * (discount / 100);
    const taxAmount = subTotal * (tax / 100);
    return (subTotal + taxAmount - discountAmount).toFixed(2);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (vehicleDetail?.vehicle_service_line_items === 0) {
        errorNotify("Atleast one Service Line Item is required!");
        return;
      }

      if (discountPercent < 0 || discountPercent > 100) {
        errorNotify("Discount must be between 0 and 100%");
        return;
      }
      if (taxPercent < 0 || taxPercent > 100) {
        errorNotify("Tax must be between 0 and 100%");
        return;
      }

      const subTotal = parseFloat(costSummary.sub_total);
      const discount = parseFloat(discountPercent) || 0;
      const tax = parseFloat(taxPercent) || 0;
      const discountAmount = subTotal * (discount / 100);
      const taxAmount = subTotal * (tax / 100);
      const complete_total = subTotal + taxAmount - discountAmount;

      const serviceData = {
        vehicle_id: parseInt(values.vehicle_id),
        odo_meter: parseInt(values.odm),
        odm: parseInt(values.odm),
        status: values.status,
        priority: values.priority,
        ref_number: values.ref_number,
        services_type: values.services_type,
        start_at: values.start_at
          ? new Date(values.start_at).toISOString()
          : null,
        complete_at: new Date(values.complete_at).toISOString(),
        note: values.note,
        cost_parts: parseFloat(costSummary.cost_parts),
        cost_labor: parseFloat(costSummary.cost_labor),
        cost_tax: taxPercent,
        discount: discountPercent,
        sub_total: subTotal,
        complete_total: complete_total,
        tags: values.tags.map((tag) => tag.value || tag),
        files: uploadedFiles.map((file) => file.fileName),
        link_issue_ids: link_issue_ids,

        line_item: vehicleDetail?.vehicle_service_line_items?.map((item) => ({
          line_item_id: item.id,
          service_issue_id: item.service_issue_id,
        })) || [],
      };

      await dispatch(createServiceEntry(serviceData)).unwrap();
      successNotify("Service entry created successfully");
      
      // Clear localStorage after successful save
      const storageKey = `checkedIssueIds_new`;
      localStorage.removeItem(storageKey);
      
      resetForm();
      setSelectedVehicle(null);
      setUploadedFiles([]);
      clearVehicleDetail();
      navigate("/services");
    } catch (error) {
      errorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  const refreshVehicleDetail = async (source = 'both') => {
    if (selectedVehicle) {
      const response = await dispatch(fetchVehicleDetail({
        vehicleId: selectedVehicle.value,
        vehicleBrandId: selectedVehicle.brand_id,
        ref_id: selectedVehicle.ref_id,
      }));
      return { response, source };
    }
  };

  return (
    <>
      <Breadcrumbs values={["Maintenance", "Overview", "New Service Entry"]} />

      <Formik
        initialValues={initialValues}
        validationSchema={serviceEntryValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form>
            <div className="access_view">
              <h2>New Service Entry</h2>

              <div className="service_entry_btn">
                <Button
                  cancelBtn={() => navigate("/services")}
                  loading={createServiceLoading}
                  disabled={createServiceLoading}
                  submitText="Save Service Entry"
                  type="submit"
                  shouldRow={false}
                />
              </div>
            </div>

            <div className="service_entry_main">
              <div className="container-fluid">
                <div className="row">
                  <div className="col-md-8">
                    <div className="service_entry_details">
                      <h6>Service Entry Details</h6>
                      <p>Comprehensive information for this service record.</p>

                      <div className="vehicle_details_fields">
                        <h5>Vehicle Details</h5>
                        <Row>
                          <Col md={6}>
                            <Select
                              label="Vehicle"
                              placeholder="Select Vehicle"
                              options={vehicleData}
                              loading={loading}
                              name="vehicle_id"
                              value={selectedVehicle}
                              onChange={(selectedOption) =>
                                handleVehicleChange(
                                  selectedOption,
                                  setFieldValue
                                )
                              }
                              onBlur={handleBlur}
                              error={errors.vehicle_id}
                              touched={touched.vehicle_id}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={6}>
                            <Select
                              label="Repair Priority"
                              placeholder="Select Repair Priority"
                              options={repairPriorityDropdown}
                              loading={false}
                              name="priority"
                              value={repairPriorityDropdown.find(
                                (option) => option.value === values.priority
                              )}
                              onChange={(selectedOption) =>
                                setFieldValue(
                                  "priority",
                                  selectedOption?.value || ""
                                )
                              }
                              onBlur={handleBlur}
                              error={errors.priority}
                              touched={touched.priority}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label="Odometer Reading"
                              placeholder="Enter Odometer Reading"
                              type="number"
                              name="odm"
                              value={values.odm}
                              showExtraText={vehicleDetail ? true : false}
                              extraText={`Last Recorded: ${values?.odo_meter || 0} KM`}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.odm}
                              touched={touched.odm}
                            />
                          </Col>
                          <Col md={6}>
                            <Select
                              label="Maintenance Type"
                              placeholder="Select Maintenance Type"
                              options={maintenanceTypesDropdown}
                              loading={false}
                              name="services_type"
                              value={maintenanceTypesDropdown.find(
                                (option) =>
                                  option.value === values.services_type
                              )}
                              onChange={(selectedOption) =>
                                setFieldValue(
                                  "services_type",
                                  selectedOption?.value || ""
                                )
                              }
                              onBlur={handleBlur}
                              error={errors.services_type}
                              touched={touched.services_type}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label="Reference Number"
                              placeholder="Enter Reference"
                              type="text"
                              name="ref_number"
                              value={values.ref_number}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.ref_number}
                              touched={touched.ref_number}
                              disabled
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="vehicle_details_fields">
                        <h5>Repair Details</h5>
                        <Row>
                          <Col md={6}>
                            <Input
                              label="Completion Date & Time"
                              placeholder=""
                              type="datetime-local"
                              name="complete_at"
                              value={values.complete_at}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.complete_at}
                              touched={touched.complete_at}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label="Start Date (Optional)"
                              placeholder=""
                              type="date"
                              name="start_at"
                              value={values.start_at}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.start_at}
                              touched={touched.start_at}
                            />
                          </Col>
                          <Col md={6}>
                            <Select
                              label="Tags"
                              placeholder="Type and press Enter to create tags"
                              options={tagsOptions}
                              loading={serviceTagsLoading}
                              name="tags"
                              value={values.tags}
                              onChange={(selectedOptions) =>
                                setFieldValue("tags", selectedOptions || [])
                              }
                              onBlur={handleBlur}
                              isMulti={true}
                              isCreatable={true}
                              error={
                                errors.tags && typeof errors.tags === "string"
                                  ? errors.tags
                                  : null
                              }
                              touched={touched.tags}
                            />
                          </Col>
                        </Row>
                      </div>
                    </div>

                    <IssueTracking
                      setLink_issue_ids={setLink_issue_ids}
                      vehicleDetailLoading={vehicleDetailLoading}
                      vehicleDetail={vehicleDetail}
                      selectedVehicle={selectedVehicle}
                      refreshVehicleDetail={() => refreshVehicleDetail('issue')}
                      checkedIssueIds={checkedIssueIds}
                      setCheckedIssueIds={setCheckedIssueIds}
                    />
                    <ServiceLineItem
                      vehicleDetailLoading={vehicleDetailLoading}
                      vehicleDetail={vehicleDetail}
                      selectedVehicle={selectedVehicle}
                      refreshVehicleDetail={() => refreshVehicleDetail('service')}
                    />

                    <div className="photo_document_section">
                      <h6>Photos & Documents</h6>
                      <p>
                        Upload relevant photos or documents for this service.
                      </p>

                      <FileUpload
                        destination="service-entry"
                        files={uploadedFiles}
                        setFiles={setUploadedFiles}
                        maxFileSize={10}
                      />
                    </div>

                    <div className="my-4">
                      <Button
                        cancelBtn={() => navigate("/services")}
                        loading={createServiceLoading}
                        disabled={createServiceLoading}
                        submitText="Save Service Entry"
                        type="submit"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="service_cost_summary_details">
                      <div className="service_cost_summary">
                        <h6>Cost Summary</h6>
                        <p>Financial breakdown of the service entries.</p>

                        <div className="subtotal">
                          <div>Subtotal (Calculated)</div>
                          <span>PKR {costSummary.sub_total}</span>
                        </div>

                        <div className="service_item">
                          <div>Parts</div>
                          <div>PKR {costSummary.cost_parts}</div>
                        </div>
                        <div className="service_item">
                          <div>Labor</div>
                          <div>PKR {costSummary.cost_labor}</div>
                        </div>
                        <div className="service_item">
                          <div>Discount (%)</div>
                          <div>
                            <input
                              className='edit_input'
                              type="number"
                              min={0}
                              max={100}
                              value={discountPercent}
                              onChange={handleDiscountChange}
                              placeholder='0'
                            /> %
                          </div>
                        </div>
                        <div className="service_item">
                          <div>Tax (%)</div>
                          <div>
                            <input
                              className='edit_input'
                              type="number"
                              min={0}
                              max={100}
                              value={taxPercent}
                              onChange={handleTaxChange}
                              placeholder='0'
                            /> %
                          </div>
                        </div>

                        <div className="total">
                          <div>Total</div>
                          <span>
                            PKR {calculateTotal()}
                          </span>
                        </div>
                      </div>

                      <div className="general_notes">
                        <h6>General Notes</h6>
                        <p>Add any additional notes or observations here.</p>

                        <textarea
                          placeholder="Type your general notes here..."
                          rows={7}
                          name="note"
                          value={values.note}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {touched.note && errors.note && (
                          <div
                            className="error-message"
                            style={{
                              color: "#ff0000de",
                              fontSize: "12px",
                              marginTop: "3px",
                            }}
                          >
                            {errors.note}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ServiceEntry;
